import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User, Loader2, Shield } from "lucide-react";
import axios from "axios";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error("Please enter username and password"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.token) {
        localStorage.setItem("adminToken", res.data.token);
        localStorage.setItem("adminUsername", res.data.username);
        toast.success("Login successful!");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid username or password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[400px]">
        <Card className="bg-white border-slate-200 shadow-lg" style={{ borderRadius: 16 }}>
          <CardContent className="p-8">
            <div className="flex justify-center mb-6">
              <Logo size="large" theme="light" />
            </div>

            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                <Shield className="w-7 h-7 text-[#6366f1]" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-[#1e293b] text-center mb-1">Admin Portal</h2>
            <p className="text-sm text-[#64748b] text-center mb-6">Enter your credentials to access the dashboard</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#1e293b] text-sm font-medium">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    type="text" placeholder="Enter username"
                    value={username} onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 h-11 bg-white border-slate-200 text-[#1e293b] placeholder:text-slate-400 focus-visible:ring-[#6366f1]"
                    data-testid="username-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1e293b] text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    type="password" placeholder="Enter password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 bg-white border-slate-200 text-[#1e293b] placeholder:text-slate-400 focus-visible:ring-[#6366f1]"
                    data-testid="password-input"
                  />
                </div>
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full h-11 font-bold text-white mt-2"
                style={{ background: "#6366f1" }}
                data-testid="login-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : <><Lock className="w-4 h-4 mr-2" />Sign In</>}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-[#64748b] hover:text-[#1e293b]">
                Back to App
              </Button>
            </div>
          </CardContent>
        </Card>
        <p className="text-center text-xs mt-6 text-[#94a3b8]">
          Protected area. Unauthorized access is prohibited.
        </p>
      </motion.div>
    </div>
  );
}
